import type {
  StaffCatalogApi,
  StaffCatalogCallOptions,
  StaffCatalogCommandOptions,
} from "@lafayette/api-client";
import type {
  StaffCatalogCategoriesBody,
  StaffCatalogDetailsInput,
  StaffCatalogExternalRefBody,
  StaffCatalogItemDetail,
  StaffCatalogListQuery,
  StaffCatalogMediaBody,
  StaffCatalogPeopleBody,
  StaffCatalogPriceAuthorizationBody,
  StaffCatalogPricingBody,
  StaffCatalogSubtypeBody,
  StaffCatalogTagsBody,
  StaffCatalogVersionBody,
  StaffCatalogVersionReasonBody,
} from "@lafayette/schemas";
import type { DataProvider } from "@refinedev/core";

// The Refine data provider of the catalog. It talks to the Lafayette API through the typed
// `StaffCatalogApi`, never to Supabase tables. Refine resources:
//   catalog-items       getList (R1, keyset pages), getOne (R2), create (W1), custom (the item commands)
//   catalog-categories  getList (R5)   catalog-tags getList (R6)   catalog-people getList (R7)
// The API pages with an opaque cursor and sends no total. A page carries `nextCursor`; the caller passes
// it back as `meta.cursor`. Update and delete are not offered: every change is a domain command.

export const catalogResources = {
  items: "catalog-items",
  categories: "catalog-categories",
  tags: "catalog-tags",
  people: "catalog-people",
} as const;

export type ItemCommandInputs = {
  updateDetails: StaffCatalogDetailsInput;
  updateSubtype: StaffCatalogSubtypeBody;
  updatePricing: StaffCatalogPricingBody;
  authorizePrice: StaffCatalogPriceAuthorizationBody;
  replaceCategories: StaffCatalogCategoriesBody;
  replaceTags: StaffCatalogTagsBody;
  replacePeople: StaffCatalogPeopleBody;
  replaceMedia: StaffCatalogMediaBody;
  addExternalRef: StaffCatalogExternalRefBody;
  markVerified: StaffCatalogVersionBody;
  submitForReview: StaffCatalogVersionBody;
  returnToDraft: StaffCatalogVersionBody;
  publish: StaffCatalogVersionBody;
  unpublish: StaffCatalogVersionReasonBody;
  archive: StaffCatalogVersionReasonBody;
};
export type ItemCommandName = keyof ItemCommandInputs;

type Executors = {
  [K in ItemCommandName]: (
    api: StaffCatalogApi,
    itemId: string,
    input: ItemCommandInputs[K],
    options: StaffCatalogCommandOptions,
  ) => Promise<StaffCatalogItemDetail>;
};

// One entry per command. A new command in the contract's client has to be named here before the UI can run it.
const executors: Executors = {
  updateDetails: (api, id, input, o) => api.updateDetails(id, input, o),
  updateSubtype: (api, id, input, o) => api.updateSubtype(id, input, o),
  updatePricing: (api, id, input, o) => api.updatePricing(id, input, o),
  authorizePrice: (api, id, input, o) => api.authorizePrice(id, input, o),
  replaceCategories: (api, id, input, o) => api.replaceCategories(id, input, o),
  replaceTags: (api, id, input, o) => api.replaceTags(id, input, o),
  replacePeople: (api, id, input, o) => api.replacePeople(id, input, o),
  replaceMedia: (api, id, input, o) => api.replaceMedia(id, input, o),
  addExternalRef: (api, id, input, o) => api.addExternalRef(id, input, o),
  markVerified: (api, id, input, o) => api.markVerified(id, input, o),
  submitForReview: (api, id, input, o) => api.submitForReview(id, input, o),
  returnToDraft: (api, id, input, o) => api.returnToDraft(id, input, o),
  publish: (api, id, input, o) => api.publish(id, input, o),
  unpublish: (api, id, input, o) => api.unpublish(id, input, o),
  archive: (api, id, input, o) => api.archive(id, input, o),
};

export const itemCommandNames = Object.keys(executors) as readonly ItemCommandName[];

/** What a command passes in `meta` (Refine's `custom` has no typed slot for it). */
export type ItemCommandMeta = Readonly<{
  command: ItemCommandName;
  itemId: string;
  idempotencyKey?: string;
  signal?: AbortSignal;
}>;

/** What a list read passes in `meta`. */
type ListMeta = Readonly<{
  query?: StaffCatalogListQuery;
  cursor?: string | null;
  limit?: number;
  q?: string;
  namespace?: string;
  signal?: AbortSignal;
}>;

function readOptions(meta: ListMeta | undefined): StaffCatalogCallOptions {
  return meta?.signal === undefined ? {} : { signal: meta.signal };
}

function unsupported(operation: string): never {
  throw new Error(
    `The catalog data provider does not support "${operation}": use a domain command.`,
  );
}

export function createLafayetteDataProvider(api: StaffCatalogApi): DataProvider {
  const provider = {
    getApiUrl: () => "",

    getList: (async ({ resource, meta }: { resource: string; meta?: unknown }) => {
      const listMeta = (meta ?? {}) as ListMeta;
      switch (resource) {
        case catalogResources.items: {
          const query: StaffCatalogListQuery = {
            ...listMeta.query,
            ...(listMeta.cursor === undefined || listMeta.cursor === null
              ? {}
              : { cursor: listMeta.cursor }),
            ...(listMeta.limit === undefined ? {} : { limit: listMeta.limit }),
          };
          const page = await api.listItems(query, readOptions(listMeta));
          return { data: page.items, total: page.items.length, nextCursor: page.nextCursor };
        }
        case catalogResources.categories: {
          const list = await api.searchCategories(
            { ...(listMeta.q === undefined ? {} : { q: listMeta.q }) },
            readOptions(listMeta),
          );
          return { data: list.items, total: list.items.length };
        }
        case catalogResources.tags: {
          const list = await api.searchTags(
            {
              ...(listMeta.q === undefined ? {} : { q: listMeta.q }),
              ...(listMeta.namespace === undefined
                ? {}
                : { namespace: listMeta.namespace as "GENERAL" }),
            },
            readOptions(listMeta),
          );
          return { data: list.items, total: list.items.length };
        }
        case catalogResources.people: {
          const page = await api.searchPeople(
            { ...(listMeta.q === undefined ? {} : { q: listMeta.q }) },
            readOptions(listMeta),
          );
          return { data: page.items, total: page.items.length, nextCursor: page.nextCursor };
        }
        default:
          return unsupported(`getList ${resource}`);
      }
    }) as DataProvider["getList"],

    getOne: (async ({ resource, id, meta }: { resource: string; id: unknown; meta?: unknown }) => {
      if (resource !== catalogResources.items) return unsupported(`getOne ${resource}`);
      const detail = await api.getItem(String(id), readOptions(meta as ListMeta | undefined));
      return { data: detail };
    }) as DataProvider["getOne"],

    create: (async ({
      resource,
      variables,
      meta,
    }: {
      resource: string;
      variables: unknown;
      meta?: unknown;
    }) => {
      if (resource !== catalogResources.items) return unsupported(`create ${resource}`);
      const key = (meta as { idempotencyKey?: string } | undefined)?.idempotencyKey;
      const detail = await api.createItem(
        variables as Parameters<StaffCatalogApi["createItem"]>[0],
        key === undefined ? {} : { idempotencyKey: key },
      );
      return { data: detail };
    }) as DataProvider["create"],

    // Every change goes through a domain command; there is no generic update or delete.
    update: (async () => unsupported("update")) as DataProvider["update"],
    deleteOne: (async () => unsupported("deleteOne")) as DataProvider["deleteOne"],

    custom: (async ({ meta, payload }: { meta?: unknown; payload?: unknown }) => {
      const command = meta as ItemCommandMeta | undefined;
      if (command === undefined || !(command.command in executors)) {
        return unsupported("custom without a known command");
      }
      const options: StaffCatalogCommandOptions = {
        ...(command.idempotencyKey === undefined ? {} : { idempotencyKey: command.idempotencyKey }),
        ...(command.signal === undefined ? {} : { signal: command.signal }),
      };
      const run = executors[command.command] as (
        api: StaffCatalogApi,
        itemId: string,
        input: unknown,
        options: StaffCatalogCommandOptions,
      ) => Promise<StaffCatalogItemDetail>;
      return { data: await run(api, command.itemId, payload, options) };
    }) as DataProvider["custom"],
  };
  // The generic method signatures of Refine's DataProvider do not fit a typed client; each method above is cast once.
  return provider as unknown as DataProvider;
}
